import { Link } from 'react-router';

import { cn } from '@/lib/cn';
import type { Brand } from '@/types/catalog';

interface BrandTileProps {
  brand: Brand;
  /** Links into the catalogue filtered by this brand. */
  to?: string;
  className?: string;
}

/**
 * A brand rendered typographically in its own colours.
 *
 * Deliberately not a logo file: reproducing third-party marks needs written
 * authorisation from each manufacturer. Swap `children` for an `<img>` here
 * once the client confirms the reseller artwork rights.
 */
export function BrandTile({ brand, to, className }: BrandTileProps) {
  const isLight = brand.bg.toUpperCase() === '#FFFFFF';

  const content = (
    <>
      <span
        className="text-center font-display text-sm leading-tight font-extrabold"
        style={{ color: brand.fg }}
      >
        {brand.name}
      </span>

      {brand.sub && (
        <span
          className="text-center text-[10px] leading-tight font-semibold opacity-80"
          style={{ color: brand.fg }}
        >
          {brand.sub}
        </span>
      )}

      {isLight && brand.accent && (
        <span
          className="mt-1 h-1 w-8 rounded-full"
          style={{ background: brand.accent }}
          aria-hidden="true"
        />
      )}
    </>
  );

  const classes = cn(
    'flex aspect-[5/3] flex-col items-center justify-center gap-1 rounded-2xl border-2 px-3 py-4',
    'shadow-card transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-card-hover',
    className,
  );

  const style = {
    background: brand.bg,
    borderColor: isLight ? (brand.accent ?? '#E8CDB4') : brand.bg,
  };

  if (!to) {
    return (
      <div className={classes} style={style}>
        {content}
      </div>
    );
  }

  return (
    <Link to={to} className={classes} style={style} aria-label={`Ver produtos ${brand.name}`}>
      {content}
    </Link>
  );
}
